import { Component, EventEmitter, Input, OnInit, OnDestroy, Output, OnChanges, SimpleChanges, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { NgForm } from '@angular/forms';
import * as moment from 'moment';
import { StudentDashboardWrapperComponent } from '../../student/student-dashboard-wrapper/student-dashboard-wrapper.component';
import { LocalizationService } from 'src/app/services/localization.service';
import { DrupalRESTService } from 'src/app/services/drupal-rest.service';
import { EntityRESTService } from 'src/app/services/entity-rest.service';
import { DialogService } from 'src/app/services/dialog.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TaxonomyService } from 'src/app/services/taxonomy.service';
import flatpickr from 'flatpickr';
import { LoggerService } from '../../../services/logger.service';

@Component({
  selector: 'app-day-view-frm-filter',
  templateUrl: './day-view-frm-filter.component.html',
  styleUrls: [
    './day-view-frm-filter.component.css',
    '../../../app.component.css'
  ]
})
export class DayViewFrmFilterComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  StudentDashboardWrapperComponent = StudentDashboardWrapperComponent;

  @Output() updateCalendarEvents: EventEmitter<string> = new EventEmitter();
  private lastSubmittedForm: NgForm | null = null;

  // Filter configuration
  @Input() filterInstructorCategory;
  @Input() filterLessonType: string = "";
  @Input() filterInstructor: string = "";
  @Input() filterStudent: string = "";
  @Input() filterDate: Date | string = "";
  @Input() filterGroupLesson: boolean = false;
  @Input() filterLesson: boolean = false;
  @Input() filterServices: boolean = false;
  @Input() filterHiddenEvents: boolean = false;

  @Input() studioCountry: string = 'US';

  @Input() frmFilterData: {}[];

  filterInstructorCategoryVal;
  filterLessonTypeVal;
  filterInstructorVal;
  filterStudentVal;
  filterDateVal;
  filterGroupLessonVal: boolean;
  filterLessonVal: boolean;
  filterServicesVal: boolean;

  // Date format helpers
  displayDateFormatAngular = 'M/d/yy';
  calendarDateFormat = 'MM/DD/YYYY';
  private displayDateTimeFormatMoment = 'dddd, M/D/YYYY, h:mm A';
  flatpickrDateFormat = 'm/d/Y';
  private flatpickrInstance: flatpickr.Instance | undefined;

  // flatpickr registers document-level listeners that hold the input (and so
  // this component and the day view) until the instance is destroyed.
  ngOnDestroy(): void {
    this.flatpickrInstance?.destroy();
    this.flatpickrInstance = undefined;
  }
  @ViewChild('filterForm', { static: false }) filterForm?: NgForm;

  @ViewChild('flatpickrInput', { static: false }) flatpickrInput?: ElementRef<HTMLInputElement>;

  constructor(
    private localizationService: LocalizationService,
    private drupalRESTService: DrupalRESTService,
    private entityRESTService: EntityRESTService,
    private dialogService: DialogService,
    private snackBar: MatSnackBar,
    public taxonomyService: TaxonomyService
  ) {}

  ngOnInit(): void {
    this.updateDateFormats();
  }

  ngAfterViewInit(): void {
    this.initializeFlatpickr();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['studioCountry']) {
      this.updateDateFormats();
    }

    if (changes['filterDate'] && !changes['filterDate'].firstChange) {
      this.updateFlatpickrDate();
    }
  }

  private updateDateFormats(): void {
    const formats = this.localizationService.getDateFormatsForCountry(this.studioCountry);
    this.displayDateFormatAngular = formats.displayDateFormatAngular;
    this.calendarDateFormat = formats.calendarDateFormat;
    this.displayDateTimeFormatMoment = formats.displayDateTimeFormatMoment;
    this.flatpickrDateFormat = formats.flatpickrDateFormat;

    if (this.flatpickrInstance) {
      this.flatpickrInstance.set('dateFormat', this.flatpickrDateFormat);
      this.updateFlatpickrDate(false);
    }
  }

  private initializeFlatpickr(): void {
    if (!this.flatpickrInput) {
      return;
    }

    this.flatpickrInstance = flatpickr(this.flatpickrInput.nativeElement, {
      dateFormat: this.flatpickrDateFormat,
      defaultDate: this.filterDate ? moment(this.filterDate).toDate() : undefined,
      disableMobile: true,
      onChange: (selectedDates) => {
        if (selectedDates && selectedDates.length > 0) {
          const selectedDate = moment(selectedDates[0]);
          if (selectedDate.isValid()) {
            this.filterDate = selectedDate.toDate();
            const targetForm = this.lastSubmittedForm ?? this.filterForm;
            if (targetForm) {
              setTimeout(() => this.onSubmit(targetForm), 0);
            }
          }
        }
      }
    });

    if (this.flatpickrInstance) {
      const originalPositionCalendar = (this.flatpickrInstance as any)._positionCalendar?.bind(this.flatpickrInstance);
      if (originalPositionCalendar) {
        (this.flatpickrInstance as any)._positionCalendar = () => {
          try {
            originalPositionCalendar();
          } catch (error: any) {
            if (!(error instanceof DOMException && error.name === 'SecurityError')) {
              throw error;
            }
          }
        };
      }

    }

    this.updateFlatpickrDate(false);
  }

  private updateFlatpickrDate(triggerChange: boolean = false): void {
    if (!this.flatpickrInstance) {
      return;
    }

    if (!this.filterDate) {
      this.flatpickrInstance.clear();
      return;
    }

    const dateMoment = moment(this.filterDate);
    if (!dateMoment.isValid()) {
      this.flatpickrInstance.clear();
      return;
    }

    this.flatpickrInstance.setDate(dateMoment.toDate(), triggerChange);
  }

  openFlatpickr(positionUnderElement?: HTMLElement): void {
    const inputEl = this.flatpickrInput?.nativeElement;
    if (!inputEl) {
      return;
    }

    if (!this.flatpickrInstance) {
      inputEl.focus();
      inputEl.dispatchEvent(new Event('click', { bubbles: true }));
      return;
    }

    this.flatpickrInstance.open();

    // If a custom position element is provided, reposition the calendar under it
    if (positionUnderElement && this.flatpickrInstance.calendarContainer) {
      setTimeout(() => {
        const rect = positionUnderElement.getBoundingClientRect();
        const calendarEl = this.flatpickrInstance!.calendarContainer;
        const calendarWidth = calendarEl.offsetWidth;

        // Center the calendar under the element
        const left = rect.left + (rect.width / 2) - (calendarWidth / 2);
        const top = rect.bottom + 8; // 8px gap below the element

        calendarEl.style.position = 'fixed';
        calendarEl.style.left = `${Math.max(8, left)}px`; // Don't go off-screen left
        calendarEl.style.top = `${top}px`;
      }, 0);
    }
  }

  // Convert the ID value to the human readable version for output.
  getNameFromId(index, Obj, key = 'value') {
    let item = Obj.find((element) => {
      return element.id == index || element.uid == index;
    });
    return item?.[key];
  }

  formatDate(date: any): string {
    // Ensure date is parsed and formatted correctly using moment
    // Use parseZone to avoid timezone conversion issues
    const parsedDate = moment.parseZone(date);
    return parsedDate.format(this.displayDateTimeFormatMoment);
  }

  // Filter Form Submit
  onSubmit(form?: NgForm) {
    const activeForm = form ?? this.filterForm;
    if (!activeForm) {
      LoggerService.warn('[TIMEZONE DEBUG] No form instance available for submission');
      return;
    }

    this.lastSubmittedForm = activeForm;
    LoggerService.log('[TIMEZONE DEBUG] onSubmit called in filter component');
    LoggerService.log('[TIMEZONE DEBUG] this.filterDate:', this.filterDate);
    LoggerService.log('[TIMEZONE DEBUG] typeof this.filterDate:', typeof this.filterDate);

    if (this.filterDate) {
      const formattedDate = this.formatDate(this.filterDate);
      LoggerService.log('[TIMEZONE DEBUG] formattedDate for display:', formattedDate);

      // Update the form value with the formatted date for display
      activeForm.value.filterDate = formattedDate;

      // Also pass the raw date for API calls to avoid timezone issues
      // Use the original date object but format it properly to preserve the intended date
      const rawDate = moment(this.filterDate).format('YYYY-MM-DD');
      LoggerService.log('[TIMEZONE DEBUG] rawDate (YYYY-MM-DD):', rawDate);
      LoggerService.log('[TIMEZONE DEBUG] filterDate as moment:', moment(this.filterDate).format('YYYY-MM-DD HH:mm:ss Z'));
      activeForm.value.filterDateRaw = rawDate;

      LoggerService.log('[TIMEZONE DEBUG] Emitting form.value:', activeForm.value);
      // Call parent function to update calendar
      this.updateCalendarEvents.emit(activeForm.value);
    } else {
      LoggerService.error('[TIMEZONE DEBUG] Invalid date:', this.filterDate);
    }
  }

  autoCompleteOptions: any = [];

  // Handle the automplete for student field.
  onStudentInput($event: { target: { value: string; }; }) {

    let term: string = $event.target.value;
    let eckType: string = "student_accounts"; // student_accounts
    let bundle: string = "student_account"; // student_account
    let field: string = "title";

    // Build the endpoint.
    let endpoint: string = "/api_rest/v1/autocomplete?";
    endpoint = endpoint + "term=" + term;
    endpoint = endpoint + "&eckType=" + eckType;
    endpoint = endpoint + "&bundle=" + bundle;
    endpoint = endpoint + "&field=" + field;

    LoggerService.log($event.target.value);
    LoggerService.log(endpoint);

    // Do the request.
    this.drupalRESTService.httpGET(endpoint)
    .subscribe(data => {
      LoggerService.log(data);

      this.autoCompleteOptions = data;
    })

    // this.drupalRESTService.httpGET(endpoint);
  }

  // Method to extract student ID from the filter input and open student dashboard
  openStudentDashboard(event?: Event) {
    // Prevent the click from triggering the autocomplete dropdown
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (!this.filterStudent) {
      return;
    }

    const studentValue = this.filterStudent.trim();

    // Extract student account ID from the format "Student Name (ID)"
    const match = studentValue.match(/\((\d+)\)$/);
    if (match && match[1]) {
      const studentAccountId = match[1];

      // First, get the student account to extract the contact ID
      this.entityRESTService.getEntity('student_accounts', 'student_account', studentAccountId)
        .subscribe({
          next: (studentAccount: any) => {
            // Extract contact ID from field_contacts (handle both single object and array)
            let contactId = null;

            if (studentAccount?.field_contacts) {
              if (Array.isArray(studentAccount.field_contacts)) {
                // If multiple contacts, use the first one
                contactId = studentAccount.field_contacts[0]?.id || studentAccount.field_contacts[0]?.target_id;
              } else {
                // Single contact object
                contactId = studentAccount.field_contacts.id || studentAccount.field_contacts.target_id;
              }
            }

            if (contactId) {
              // Open dashboard with the contact ID
              this.dialogService.openDialog(this.StudentDashboardWrapperComponent, 'largerScreenDashboard', { student_id: contactId });
            } else {
              LoggerService.error('No contact ID found in student account:', studentAccount);
              if (this.snackBar) {
                this.snackBar.open('Unable to find student contact information', 'Close', {
                  duration: 3000,
                });
              }
            }
          },
          error: (error) => {
            LoggerService.error('Error fetching student account:', error);
            if (this.snackBar) {
              this.snackBar.open('Error loading student information', 'Close', {
                duration: 3000,
              });
            }
          }
        });
    }
  }
}
